import {
  ApiRequests,
  ApiRequestsTypes,
  DatabaseMastheadIcon,
  FeatureFlagsContext,
  getDatabaseIconTooltip,
  PlaceHeaderMetadataItem,
  PlacePageActionsMenu,
  PlacePageHeader,
  PlacePageMastheadContainer,
  PlacePageMastheadRowContainer,
  PlacePageTitle,
  PlacePageTitleContainer,
  SiteUrlsContext,
  UIActionsContext,
} from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { QueryDatabaseOwnDataQualityWarnings } from '../DataQualityWarnings/DataQualityWarningDatabases';
import {
  CreateDocumentFromResourceButton,
  RESOURCE_TYPE_DATABASE,
} from '../TablePlacePage/CreateWorkbookFromCatalogButtons';
import { DatabasesDisplayPlacePageData } from '../database/types/DatabasesDisplayPlacePageData';
import { PlaceHeaderMetadataCertification } from '../placePage/PlaceHeaderMetadataCertification';
import { PlaceHeaderMetadataProject } from '../placePage/PlaceHeaderMetadataProject';
import { PlaceHeaderMetadataTableauUser } from '../placePage/PlaceHeaderMetadataTableauUser';
import { PlacePageIconContainer } from '../placePage/PlacePageIconContainer';

export type DatabasePlacePageHeaderProps = {
  readonly apiRequests: ApiRequests;
  readonly database: DatabasesDisplayPlacePageData;
  readonly hideLaunchEditOnMobile?: boolean;
  readonly newFlowFromCatalog?: boolean;
  readonly projectIdForNewDocuments?: string;
};

export function DatabasePlacePageHeader(
  props: DatabasePlacePageHeaderProps
): JSX.Element {
  const uiActions = React.useContext(UIActionsContext);
  const siteUrls = React.useContext(SiteUrlsContext);

  const editingIsEnabled: boolean =
    props.apiRequests.isSiteCreatorOrHigher() && !props.hideLaunchEditOnMobile;
  const canCreateFlowFromDatabase: boolean =
    editingIsEnabled && !!props.newFlowFromCatalog;

  return (
    <FeatureFlagsContext.Consumer>
      {
        featureFlags =>
          <PlacePageHeader>
            <PlacePageTitleContainer>
              <PlacePageIconContainer title={getDatabaseIconTooltip(props.database)}>
                <DatabaseMastheadIcon database={props.database} />
              </PlacePageIconContainer>
              <PlacePageMastheadContainer>
                <PlacePageMastheadRowContainer>
                  <PlacePageTitle title={props.database.name || undefined}>
                    {props.database.name}
                  </PlacePageTitle>
                  <PlacePageActionsMenu>
                    {uiActions.renderDatabasesActionMenu(props.database)}
                  </PlacePageActionsMenu>
                </PlacePageMastheadRowContainer>
                <PlacePageMastheadRowContainer>
                  <DatabaseContact database={props.database} />
                  {featureFlags.databasesInProjectsEnabled && <DatabaseProject database={props.database} apiRequests={props.apiRequests} siteUrls={siteUrls} />}
                  <DatabaseCertifier database={props.database} siteUrls={siteUrls} />
                  <QueryDatabaseOwnDataQualityWarnings nodeId={props.database.id} />
                </PlacePageMastheadRowContainer>
              </PlacePageMastheadContainer>
            </PlacePageTitleContainer>
            <CreateDocumentFromResourceButton
              apiRequests={props.apiRequests}
              siteUrls={siteUrls}
              isEmbedded={!!props.database.isEmbedded}
              resourceId={props.database.id}
              resourceType={RESOURCE_TYPE_DATABASE}
              databaseClass={props.database.connectionType}
              testId={'CreateDocumentFromDatabaseButton'}
              title={Messages.NewWorkbookButton()}
              canCreateWorkbook={editingIsEnabled}
              canCreateFlow={canCreateFlowFromDatabase}
              projectIdForNewDocuments={props.projectIdForNewDocuments}
            />
          </PlacePageHeader>
      }
    </FeatureFlagsContext.Consumer>
  );
}

function DatabaseContact(props: {
  database: DatabasesDisplayPlacePageData;
}): JSX.Element {
  return (
    <PlaceHeaderMetadataItem>
      <PlaceHeaderMetadataTableauUser
        tableauUser={props.database.contact}
        labelText={Messages.GridHeaderContact()}
      />
    </PlaceHeaderMetadataItem>
  );
}

//TODO: when back-end certification unification feature flag is gone, update the query to no longer use certification note
function DatabaseCertifier(props: {
  database: DatabasesDisplayPlacePageData;
  siteUrls: SiteUrls;
}): JSX.Element | null {
  return (
    <PlaceHeaderMetadataItem>
      <PlaceHeaderMetadataCertification
        anchoredAsset={{
          __typename: 'DataQualityCertification',
          asset: { ...props.database },
        }}
        subjectArea={props.database.__typename}
        vizportalId={props.database.vizportalId}
        contentId={props.database.id}
        certification={props.database}
        siteUrls={props.siteUrls}
        showExtractRefresh={false}
        dataQualityCertifications={props.database.dataQualityCertifications}
      />
    </PlaceHeaderMetadataItem>
  );
}

export function DatabaseProject(props: {
  database: DatabasesDisplayPlacePageData,
  apiRequests: ApiRequests,
  siteUrls: SiteUrls
}): JSX.Element | null {
  const [container, setContainer] = React.useState<ApiRequestsTypes.GetContainerForContentResult>();

  React.useEffect(
    () => {
      props.apiRequests
        .getContainerForContent({ contentType: 'database', contentId: props.database.vizportalId })
        .then(container => setContainer(container))
        .catch(() => setContainer(undefined));
    },
    [props.database, props.apiRequests]
  );

  return (container && container.type === 'PROJECT')
    ? (
      <PlaceHeaderMetadataItem>
        <PlaceHeaderMetadataProject projectId={container.id} projectName={container.name} siteUrls={props.siteUrls} />
      </PlaceHeaderMetadataItem>
    )
    : null;
}
